import { useState, useEffect, useRef, cloneElement } from 'react';
import { CSSTransition } from 'react-transition-group';
import clsx from 'clsx';
import useWindowSize from '@/hooks/useWindowSize';
import useLockedBody from '@/hooks/useLockedBody';
import '@/components/modal/modal.css';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ModalSlice } from '@/redux/modal';

/**
 * :TODO Check how newClient additionalTerms works
 */

const Modal = () => {
  const dispatch = useAppDispatch();
  const [modifiers, setModifiers] = useState([]);
  const [bodyHeight, setBodyHeight] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const isOpen = useAppSelector(ModalSlice.selectors.isOpen);
  const modifier = useAppSelector(ModalSlice.selectors.modifier);
  const ModalHeadContent = useAppSelector(ModalSlice.selectors.head);
  const ModalMainContent = useAppSelector(ModalSlice.selectors.main);

  const overlayRef = useRef(null);
  const bodyRef = useRef(null);
  const headRef = useRef(null);
  const mainRef = useRef(null);
  const closeRef = useRef(null);

  const { setLocked } = useLockedBody();

  const { width, height } = useWindowSize();

  const setScrollData = () => {
    if (mainRef.current) {
      setScrollHeight(Math.round(mainRef.current.scrollHeight));
      setScrollTop(Math.round(mainRef.current.scrollTop));
    }
  };

  useEffect(() => {
    setLocked(isOpen);
    if (isOpen && mainRef.current) {
      mainRef.current.addEventListener('scroll', setScrollData);
    }
    return () => {
      if (mainRef.current) {
        mainRef.current.removeEventListener('scroll', setScrollData);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (bodyRef.current) {
      setBodyHeight(Math.round(bodyRef.current.getBoundingClientRect().height));
    }
    setScrollData();
  }, [width, height]);

  const modalBody = (...modifiers) =>
    clsx('modal__body', {
      ...modifiers.reduce((obj, modifier) => {
        return Object.assign(obj, { [`modal__body--${modifier}`]: modifier });
      }, {}),
    });

  const clickOverlay = (e) => {
    if (e.target === overlayRef.current) {
      if (location.hash.indexOf('cmp_return_home') > -1) {
        window.history.back();
        return;
      }
      dispatch(ModalSlice.actions.closeModal());
    }
  };

  const onCloseButton = () => {
    if (location.hash.indexOf('cmp_return_home') > -1) {
      window.history.back();
      return;
    }
    dispatch(ModalSlice.actions.closeModal());
  };

  const onScroll = () => {
    if (mainRef.current) {
      const scrollHeight = mainRef.current.scrollHeight;
      const scrollTop = mainRef.current.scrollTop;
      const clientHeight = mainRef.current.clientHeight;
      const bottom = scrollHeight - scrollTop <= clientHeight + 4;
      if (bottom) {
        dispatch(ModalSlice.actions.setIsModalBottomReached(true));
      }
    }
  };

  const updateModalModifiers = ({ add, remove }) => {
    const newModifiers = add || [];
    const updatedModifiers = Array.from(new Set([modifier, ...modifiers, ...newModifiers])).filter((m) => m);
    const filteredModifiers = updatedModifiers.filter((m) => {
      if (m && remove) {
        return remove.indexOf(m) === -1;
      } else {
        return true;
      }
    });
    setModifiers(filteredModifiers);
  };

  const onEnteredHandler = () => {
    if (bodyRef.current) {
      setBodyHeight(Math.round(bodyRef.current.getBoundingClientRect().height));
    }
  };

  const onExitedHandler = () => {
    setModifiers([]);
    dispatch(ModalSlice.actions.clearModalContent());
  };

  return (
    <CSSTransition
      in={isOpen}
      timeout={{ enter: 600, exit: 1050 }}
      nodeRef={overlayRef}
      onEntered={onEnteredHandler}
      onExited={onExitedHandler}
      classNames='modal'
      unmountOnExit
    >
      <div
        data-testid='modal'
        data-modal-name={ModalMainContent?.type?.name}
        className='modal'
        id='modal'
        ref={overlayRef}
        onClick={clickOverlay}
      >
        <div data-testid='modal-body' ref={bodyRef} className={modalBody(modifier, ...modifiers)}>
          <div data-testid='modal-action' className='modal__action'>
            <button
              data-testid='modal-close'
              className='modal__close'
              ref={closeRef}
              type='button'
              onClick={onCloseButton}
            />
          </div>
          {ModalHeadContent && (
            <div data-testid='modal-head' ref={headRef} className='modal__head'>
              {ModalHeadContent}
            </div>
          )}
          {ModalMainContent && (
            <div data-testid='modal-main' className='modal__main'>
              <div className='modal__content' ref={mainRef} onScroll={onScroll}>
                {cloneElement(ModalMainContent, {
                  onUpdateModifiers: updateModalModifiers,
                  modalHeight: bodyHeight,
                  scrollTop: scrollTop,
                  scrollHeight: scrollHeight,
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </CSSTransition>
  );
};

Modal.displayName = 'Modal';

export default Modal;
